import React, { memo, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import TextField from 'modules/views/shared/TextField';
import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import PasswordField from 'modules/views/shared/PasswordField';
import NavigationButtons from 'modules/views/shared/NavigationButtons';

import { useWizardStepNavigation } from 'modules/views/shared/Wizard/hooks';
import useForm from 'hooks/useForm';
import useSubmitOnEnter from 'hooks/useSubmitOnEnter';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import createPasswordSchema from './schema';
import {
  createPassword,
  fetchAllCertifications,
  fetchCertificationGroups,
  fetchWorkerDetails,
  setOnboarding,
  assignExistingWorkerToCompanies,
} from 'modules/state-mgmt/onboarding';
import { getCreatePasswordParams } from './utils';
import { authenticateUser } from 'modules/state-mgmt/user';
import useLoadAllData from 'hooks/useLoadAllData';
import { getBackendErrorsFromResponse, prettifyErrorResponse } from 'utils';

import { actionMapForOtherLoaders } from 'constants/actions';
import { WorkerInvitationStatus } from 'models/general';
import { ROUTES } from 'constants/routes';

const CreatePassword = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { goToNextStep } = useWizardStepNavigation();
  const {
    onboarding,
    user,
    languageCode,
    createPasswordLoading,
    authenticateUserLoading,
    fetchWorkerDetailsLoading,
    fetchCertificationGroupsLoading,
    fetchAllCertificationsLoading,
    isOffline,
    isWorker,
  } = useAppSelector(state => ({
    onboarding: state.onboarding,
    user: state.user,
    languageCode: state.app?.languageCode,
    createPasswordLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.createPassword],
    authenticateUserLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.authenticateUser],
    fetchWorkerDetailsLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.fetchWorkerDetails],
    fetchCertificationGroupsLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.fetchCertificationGroups],
    fetchAllCertificationsLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.fetchAllCertifications],
    isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
    isWorker: !state.user.userId || state.user.isWorker,
  }));
  const isAdmin = user.isAdmin;
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [{ isLoading, hasLoaded, errors: loadErrors }, loadAllData] = useLoadAllData();

  const getWorkerDetails = async () => {
    try {
      await Promise.all([
        dispatch(fetchWorkerDetails({ workerId: onboarding.id, languageCode })).unwrap(),
        dispatch(
          fetchCertificationGroups({
            projectId: onboarding.temp?.project?.value,
            workerId: onboarding.id,
            languageCode,
          }),
        ).unwrap(),
        dispatch(fetchAllCertifications({ workerId: onboarding.id, languageCode })).unwrap(),
        dispatch(
          assignExistingWorkerToCompanies({
            projectId: onboarding.temp?.project?.value,
            employers: onboarding.temp?.employers || [],
            languageCode,
          }),
        ).unwrap(),
      ]);

      goToNextStep();
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  const handleWorkerSignInAndDataFetching = (password: string) => {
    dispatch(authenticateUser({ password }))
      .then(res => {
        if (res.type.includes('fulfilled')) {
          dispatch(
            assignExistingWorkerToCompanies({
              projectId: onboarding.temp?.project?.value,
              employers: onboarding.temp?.employers || [],
              languageCode,
            }),
          );
          loadAllData();
        } else {
          enqueueSnackbar(prettifyErrorResponse(res?.payload), {
            variant: 'error',
          });
        }
      })
      .catch(err => {
        enqueueSnackbar(prettifyErrorResponse(err), { variant: 'error' });
      });
  };

  const handleCreatePassword = (values: any) => {
    if (isOffline && !isWorker) {
      dispatch(
        setOnboarding({
          invitationStatus: WorkerInvitationStatus.ACCEPTED,
          temp: {
            ...onboarding.temp,
            password: values.password,
          },
          uploadingDetails: {
            ...onboarding.uploadingDetails,
            isOffline: true,
            pendingUploads: {
              ...onboarding.uploadingDetails?.pendingUploads,
              workerPasswordCreation: true,
              employers: true,
            },
            errors:
              onboarding.uploadingDetails?.errors?.pageUrl === ROUTES.CREATE_PASSWORD.path
                ? { pageUrl: '', errorObject: null }
                : onboarding.uploadingDetails?.errors,
          },
        }),
      );
      if (onboarding?.pictureUrl) {
        navigate(ROUTES.PERSONAL_INFORMATION.path);
      } else {
        goToNextStep();
      }
      return;
    }

    dispatch(
      createPassword(
        getCreatePasswordParams({
          ...(onboarding || {}),
          ...values,
          languageCode: languageCode,
          isSelfRegistered: user.isWorker || !user.userId,
        }),
      ),
    ).then(res => {
      if (res.type.includes('fulfilled')) {
        if (!user?.userId) {
          handleWorkerSignInAndDataFetching(values.password);
        } else {
          getWorkerDetails();
        }
      } else {
        enqueueSnackbar(prettifyErrorResponse(res.payload), { variant: 'error' });
      }
    });
  };

  const { values, errors, handleSubmit, setValue, setErrors } = useForm({
    initialValues: {
      password: onboarding.temp?.password || '',
      confirmPassword: onboarding.temp?.password || '',
    },
    validationSchema: createPasswordSchema(t, {
      email: onboarding.email,
      mobilePhoneNumber: onboarding.mobilePhoneNumber,
    }),
    onSubmit: handleCreatePassword,
  });

  const fields = useMemo(
    () => [
      {
        name: t('createPassword.password'),
        id: 'password',
        isRequired: true,
      },
      {
        name: t('createPassword.confirmPassword'),
        id: 'confirmPassword',
        isRequired: true,
      },
    ],
    [t],
  );

  // On mount, populate form errors from background upload errors (if any)
  useEffect(() => {
    const uploadErrors = onboarding?.uploadingDetails?.errors;
    if (uploadErrors?.pageUrl === ROUTES.CREATE_PASSWORD.path && uploadErrors?.errorObject) {
      setErrors(getBackendErrorsFromResponse(uploadErrors.errorObject, ['password', 'confirmPassword']));
    }
  }, []);

  useEffect(() => {
    if (!isLoading && isLoadingData && !fetchWorkerDetailsLoading?.isLoading) {
      setIsLoadingData(false);
      if (hasLoaded && (!loadErrors || loadErrors?.length === 0)) {
        goToNextStep();
      } else {
        enqueueSnackbar(loadErrors?.map(error => prettifyErrorResponse(error))?.join(' '), { variant: 'error' });
      }
    }
    if (!isLoadingData && isLoading) {
      setIsLoadingData(true);
    }
  }, [isLoading, isLoadingData, hasLoaded, loadErrors, fetchWorkerDetailsLoading?.isLoading, goToNextStep]);

  const showLoading =
    createPasswordLoading?.isLoading ||
    authenticateUserLoading?.isLoading ||
    fetchWorkerDetailsLoading?.isLoading ||
    fetchAllCertificationsLoading?.isLoading ||
    fetchCertificationGroupsLoading?.isLoading ||
    isLoading ||
    isLoadingData;
  const submitOnEnter = useSubmitOnEnter(handleSubmit, showLoading);

  const content = (
    <div>
      <ContentHeader title={t('createPassword.title')} subtitle={t('createPassword.subtitle')} />
      <Grid container spacing={3} style={{ marginTop: 28 }}>
        <Grid item xs={12}>
          <PasswordField
            label={t('createPassword.password')}
            name="password"
            required={true}
            value={values.password}
            email={onboarding.email}
            mobilePhoneNumber={onboarding.mobilePhoneNumber}
            testId="password-input"
            onChange={e => setValue('password', e.target.value)}
            maskText={isAdmin}
            autoComplete={isAdmin ? 'off' : 'new-password'}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('createPassword.confirmPassword')}
            name="confirmPassword"
            required={true}
            value={values.confirmPassword}
            error={errors.confirmPassword?.message}
            type={isAdmin ? 'text' : 'password'}
            maskText={isAdmin}
            testId="confirmPassword-input"
            onChange={e => setValue('confirmPassword', e.target.value)}
            onKeyDown={submitOnEnter}
            autoComplete={isAdmin ? 'off' : 'new-password'}
          />
        </Grid>
      </Grid>
      <NavigationButtons
        secondaryButtonProps={{
          isHidden: true,
        }}
        primaryButtonProps={{
          isLoading: showLoading,
          hasEndArrow: true,
          label: t('createPassword.buttons.createAccount'),
          testId: 'create-account-button',
          onClick: handleSubmit,
        }}
      />
    </div>
  );

  return <WithWizard disableNavigation={showLoading} fields={fields} content={content} />;
};

export default memo(CreatePassword);
